const AUDIENCES = ["customer", "internal"];
const UTC_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|\+00:00)$/;

const WITHDRAW_KEYS = [
  "factId",
  "revision",
  "topicKey",
  "text",
  "language",
  "audience",
  "ownerRef",
  "sourceRef",
  "sourceRevision",
  "expectedRevision",
];

function snapshotValue(value) {
  if (value == null) {
    return "";
  }
  return String(value);
}

export function knowledgeWithdrawSnapshot(fields) {
  const source = fields && typeof fields === "object" ? fields : {};
  const snapshot = {};
  WITHDRAW_KEYS.forEach((key) => {
    snapshot[key] = snapshotValue(source[key]);
  });
  return snapshot;
}

function sameSnapshot(left, right) {
  return WITHDRAW_KEYS.every((key) => left[key] === right[key]);
}

export function resolveKnowledgeWithdrawCommand(previous, fields, createCommand) {
  const snapshot = knowledgeWithdrawSnapshot(fields);
  if (
    previous
    && typeof previous.commandId === "string"
    && previous.commandId !== ""
    && typeof previous.asOf === "string"
    && previous.asOf !== ""
    && previous.fields
    && sameSnapshot(previous.fields, snapshot)
  ) {
    return previous;
  }
  const created = createCommand();
  return {
    commandId: created.commandId,
    asOf: created.asOf,
    fields: snapshot,
  };
}

function requiredText(value) {
  if (typeof value !== "string" || value === "") {
    return null;
  }
  return value;
}

function optionalText(value) {
  if (value == null || value === "") {
    return null;
  }
  if (typeof value !== "string") {
    return undefined;
  }
  return value;
}

function wholeNumber(value) {
  if (typeof value === "number") {
    if (!Number.isSafeInteger(value) || value < 0) {
      return undefined;
    }
    return value;
  }
  if (value == null || value === "") {
    return null;
  }
  if (typeof value !== "string" || !/^(0|[1-9]\d*)$/.test(value)) {
    return undefined;
  }
  const parsed = Number(value);
  if (!Number.isSafeInteger(parsed)) {
    return undefined;
  }
  return parsed;
}

function utcInstant(value) {
  if (typeof value !== "string" || !UTC_INSTANT.test(value)) {
    return null;
  }
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) {
    return null;
  }
  return value;
}

export function buildKnowledgeWithdrawBody({ commandId, asOf, fields }) {
  const source = fields && typeof fields === "object" ? fields : {};
  const factId = requiredText(source.factId);
  const topicKey = requiredText(source.topicKey);
  const text = requiredText(source.text);
  const language = requiredText(source.language);
  const audience = requiredText(source.audience);
  const ownerRef = requiredText(source.ownerRef);
  const sourceRef = optionalText(source.sourceRef);
  const sourceRevision = optionalText(source.sourceRevision);
  const asOfInstant = utcInstant(asOf);
  const revision = wholeNumber(source.revision);
  const expectedRevision = wholeNumber(source.expectedRevision);
  if (
    typeof commandId !== "string"
    || commandId === ""
    || asOfInstant == null
    || factId == null
    || topicKey == null
    || text == null
    || language == null
    || audience == null
    || !AUDIENCES.includes(audience)
    || ownerRef == null
    || sourceRef === undefined
    || sourceRevision === undefined
    || revision == null
    || expectedRevision === undefined
  ) {
    return { error: "empty" };
  }
  const fact = {
    fact_id: factId,
    revision,
    topic_key: topicKey,
    approved_text_or_typed_value: text,
    language,
    audience,
    owner_ref: ownerRef,
    status: "withdrawn",
  };
  if (sourceRef != null) {
    fact.source_ref = sourceRef;
  }
  if (sourceRevision != null) {
    fact.source_revision = sourceRevision;
  }
  const body = {
    command_id: commandId,
    as_of: asOfInstant,
    fact,
  };
  if (expectedRevision != null) {
    body.expected_revision = expectedRevision;
  }
  return { body };
}

export function knowledgeWithdrawSaveFollowUp({ outcome, status, reloadFailed, detail }) {
  if (outcome === "applied" || outcome === "replayed") {
    if (reloadFailed) {
      return {
        saved: true,
        replaceList: false,
        notice: "Withdraw accepted. The list could not be reloaded.",
      };
    }
    return { saved: true, replaceList: true, notice: "Withdraw accepted." };
  }
  const refused = status === 403 || status === 409 || status === 422;
  const notice = refused && typeof detail === "string" && detail !== ""
    ? detail
    : "Withdraw was not saved.";
  if (refused && reloadFailed) {
    return {
      saved: false,
      replaceList: false,
      reload: false,
      notice: `${notice} The list could not be reloaded.`,
    };
  }
  return { saved: false, replaceList: false, reload: refused, notice };
}
